import type { MidiStatus } from '../midi/MidiBus';
import {
  OUTPUT_SELECTABLE,
  SYSTEM_DEFAULT,
  deviceLabel,
  openInput,
  releaseInput,
  selectInput,
  selectOutput,
  selectedLabel,
  useDeviceStore,
  type AudioDevice,
} from '../audio/devices';
import { saveSample } from '../audio/synth';
import { useAppStore } from '../store/appStore';
import { usePanelStore } from '../store/panelStore';
import { USER_SAMPLE_SLOT } from '../store/panelTypes';
import { Picker, type PickerOption } from './Picker';

const SAMPLE_NAMES = [
  'Piano',
  'Strings',
  'Pizzicato',
  'Bass Guitar',
  'Glow',
  'Your Sample',
  'Airdock',
  'Synthwave',
  'Church Organ',
  'FM Ko',
  'FM Bell',
  '808',
];

/** The bindings that have no button to sit on, spelt out once instead. */
const KEY_LEGEND = '\u2190 \u2192 octave \u00b7 scroll data entry \u00b7 \u2318Z undo';

/** `All inputs` is the bus's default, so it needs an id of its own to pick. */
const EVERY_MIDI_INPUT = '*';

function deviceOptions(devices: AudioDevice[], kind: string): PickerOption[] {
  return [
    { id: SYSTEM_DEFAULT, label: 'System default' },
    ...devices.map((device, index) => ({ id: device.id, label: deviceLabel(device, index, kind) })),
  ];
}

function describeMidi(status: MidiStatus, inputs: string[]): string {
  switch (status) {
    case 'ready':
      return inputs.length > 0 ? inputs.join(', ') : 'No devices';
    case 'denied':
      return 'Blocked';
    case 'unsupported':
      return 'Unsupported';
    default:
      return 'Connecting';
  }
}

/**
 * Status line: what is loaded, the tempo, and where sound and MIDI are coming
 * and going. The three device fields are pickers rather than readouts.
 */
export function Overlay() {
  const phase = useAppStore((state) => state.phase);
  const midiStatus = useAppStore((state) => state.midiStatus);
  const midiInputs = useAppStore((state) => state.midiInputs);
  const midiInputId = useAppStore((state) => state.midiInputId);
  const selectMidiInput = useAppStore((state) => state.selectMidiInput);
  const helpOpen = useAppStore((state) => state.helpOpen);
  const toggleHelp = useAppStore((state) => state.toggleHelp);

  const slot = usePanelStore((state) => state.SAMPLE_SELECTION_VALUE);
  const bpm = usePanelStore((state) => state.BPM);

  const inputs = useDeviceStore((state) => state.inputs);
  const outputs = useDeviceStore((state) => state.outputs);
  const inputId = useDeviceStore((state) => state.inputId);
  const outputId = useDeviceStore((state) => state.outputId);
  const named = useDeviceStore((state) => state.named);

  // Nothing to say while the loader is up; it has the screen to itself.
  if (phase === 'loading') return null;

  // The hint arrives after the title card has burnt on and leaves with it, so
  // it stays mounted through the dissolve rather than cutting out on the click.
  if (phase !== 'playing') {
    const leaving = phase === 'dissolving' ? ' overlay--leaving' : '';
    return (
      <div className={`overlay overlay--intro${leaving}`}>
        <p className="hint">Click to begin</p>
      </div>
    );
  }

  const midiReady = midiStatus === 'ready' && midiInputs.length > 0;
  const midiOptions: PickerOption[] = [
    { id: EVERY_MIDI_INPUT, label: 'All inputs' },
    ...midiInputs.map((port) => ({ id: port.id, label: port.name })),
  ];
  const midiValue = midiReady
    ? (midiInputs.find((port) => port.id === midiInputId)?.name ?? 'All inputs')
    : describeMidi(
        midiStatus,
        midiInputs.map((port) => port.name),
      );

  const recorded = slot === USER_SAMPLE_SLOT;

  return (
    <div className="overlay">
      {helpOpen && <p className="legend">{KEY_LEGEND}</p>}
      <dl className="status">
        <div className="field">
          <dt>Sample</dt>
          <dd>{recorded ? 'Your Sample' : (SAMPLE_NAMES[slot] ?? '—')}</dd>
        </div>
        <div className="field">
          <dt>Tempo</dt>
          <dd>{bpm} bpm</dd>
        </div>

        <Picker
          label="In"
          value={selectedLabel(inputs, inputId, 'Input')}
          options={deviceOptions(inputs, 'Input')}
          selectedId={inputId}
          onSelect={selectInput}
          // Device names stay blank until the microphone has been granted once,
          // so the first open asks for it and hands the stream straight back.
          onOpen={named ? undefined : () => void openInput().then(releaseInput)}
        />
        <Picker
          label="Out"
          value={OUTPUT_SELECTABLE ? selectedLabel(outputs, outputId, 'Output') : 'Follows system'}
          options={deviceOptions(outputs, 'Output')}
          selectedId={outputId}
          onSelect={(id) => void selectOutput(id)}
          disabled={!OUTPUT_SELECTABLE}
        />
        <Picker
          label="Midi"
          value={midiValue}
          options={midiOptions}
          selectedId={midiInputId ?? EVERY_MIDI_INPUT}
          onSelect={(id) => selectMidiInput(id === EVERY_MIDI_INPUT ? null : id)}
          disabled={!midiReady}
        />

        <div className="field">
          <dt>Keys</dt>
          <dd>
            <button
              type="button"
              className="pick pick--plain"
              aria-pressed={helpOpen}
              onClick={toggleHelp}
            >
              {helpOpen ? 'Hide' : 'Show'}
            </button>
          </dd>
        </div>
        <div className="field">
          <dt>Take</dt>
          <dd>
            <button
              type="button"
              className="pick"
              disabled={!recorded}
              title={recorded ? 'Download as WAV' : 'Record a sample first'}
              onClick={() => saveSample()}
            >
              {recorded ? 'Save WAV' : '—'}
            </button>
          </dd>
        </div>
      </dl>
    </div>
  );
}
